import { useRef, useState } from "react";
import { ApiError } from "@/lib/api/client";
import { uuidv7 } from "@/lib/uuid";
import { usePrivateApi } from "@/shared/usePrivateApi";

type Pending = { key: string; id: string };

/**
 * One idempotent till dispatch command at a time (plan D-10; the same rule as
 * `AssignDriverSheet`, B3 · P13):
 *
 *  - every POST carries a client UUIDv7 `id` and `clientTimestamp`;
 *  - a request whose answer was LOST (a network failure, `fetch` throwing a
 *    non-`ApiError`) keeps its id, so the cashier's retry of the SAME action
 *    (same `key`) is a replay the server answers `applied: false` — a cash
 *    confirm can never settle twice;
 *  - any definite answer — success or a typed refusal — retires the id; a
 *    different action (another key) always gets a fresh one.
 *
 * `busy` is the key in flight, for the button's spinner.
 */
export function useDispatchCommand() {
  const { apiFetch } = usePrivateApi();
  const pending = useRef<Pending | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const commandId = (key: string): string => {
    if (pending.current?.key !== key) pending.current = { key, id: uuidv7() };
    return pending.current.id;
  };

  const send = async <T>(key: string, path: string, body: Record<string, unknown>): Promise<T> => {
    setBusy(key);
    try {
      const result = await apiFetch<T>(path, {
        method: "POST",
        body: { id: commandId(key), clientTimestamp: new Date().toISOString(), ...body },
      });
      pending.current = null;
      return result;
    } catch (error) {
      if (error instanceof ApiError) pending.current = null;
      throw error;
    } finally {
      setBusy(null);
    }
  };

  return { send, busy };
}
